"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Chart = void 0;
const d3_1 = require("d3");
const DatasetEntityArrayManager_1 = __importDefault(require("./managers/DatasetEntityArrayManager"));
const AxesManager_1 = __importDefault(require("./managers/AxesManager"));
const TooltipManager_1 = __importDefault(require("./managers/TooltipManager"));
const TooltipPoint_1 = require("./entities/TooltipPoint");
const DataVisualStyle_1 = require("./enums/DataVisualStyle");
const LabelPosition_1 = require("./enums/LabelPosition");
const LegendOrientation_1 = require("./enums/LegendOrientation");
const ChartServices_1 = require("./enums/ChartServices");
const Services_1 = require("./enums/Services");
const AxisPosition_1 = require("./enums/AxisPosition");
const ServiceNames_1 = require("./enums/ServiceNames");
const TOOLTIP_DEFAULT_OFFSET_TABLET = 0;
const TOOLTIP_DEFAULT_OFFSET_DESKTOP = 190;
const TABLET_BREAKPOINT = 1024;
const TOOLTIP_X_PADDING = 10;
const AVERAGE_TOOLTIP_WIDTH = 160;
const TOOLTIP_Y_PADDING = 15;
const bisectDate = (0, d3_1.bisector)(function (d) {
    return d.time;
}).left;
class Chart {
    constructor(chartBuilder) {
        this.ChartServices = ChartServices_1.ChartServices;
        this._chartContainer = {
            svgElement: null,
            chartArea: null,
            chartBody: null,
            focusLine: null,
            previewChart: null,
            zoomableArea: null,
            focusCircles: [],
            zoom: null,
            brush: null
        };
        this._axisScales = [];
        this._axisScalesInput = [];
        this._tooltipWidth = AVERAGE_TOOLTIP_WIDTH;
        this._valueRangeArray = [];
        this._loggerFilters = [];
        this._chartBuilder = chartBuilder;
        this._chartId = chartBuilder.chartId;
        this._colorGenerator = chartBuilder.colorGenerator;
        this._axesManager = new AxesManager_1.default(chartBuilder.xAxisTickSize, chartBuilder.yAxisTickSize, chartBuilder.gridTickSize, chartBuilder.chartDateFormatter);
        this._chartConfiguration = chartBuilder.chartConfiguration;
        this._settings = chartBuilder.settings;
        this._element = chartBuilder.element;
        this._servicesManager = chartBuilder.services;
        this._dataManager = chartBuilder.dataManager;
        this._dateRange = chartBuilder.dateRange;
        this._fontSize = chartBuilder.fontSize;
        this._axisThickness = chartBuilder.axisThickness;
        this._boundaryManager = chartBuilder.boundaryManager;
        this._eventDatasetManager = chartBuilder.eventDatasetManager;
        this._labelPosition = chartBuilder.labelPosition;
        this._legendOrientation = chartBuilder.legendOrientation;
        this._dateFormatter = chartBuilder.dateFormatter;
        this._onlyTimeFormatter = chartBuilder.onlyTimeFormatter;
        this._onlyDateFormatter = chartBuilder.onlyDateFormatter;
        this._axisScalesInput = chartBuilder.axisScaleRange;
        this._valueRangeArray = chartBuilder.valueRangeArray;
        this._initialService = this.getServiceParam(chartBuilder.initService);
        this._loggerFilters = this.getLoggersFilterParam();
        this.createChart();
        this.loadSelectedloggers();
        this.scaleYaxis();
        this.rescaleElements();
        this.loadSelectedService(this._initialService);
    }
    createChart() {
        this._fullWidth = this.getElementWidth(this._element);
        this._chartContainer.svgElement = this.createSvgElement(this._element, this._settings.svgHeight, this._fullWidth);
        this._dimensions = this.countChartDimensions(this._chartContainer.svgElement);
        this.createChartContainer();
        this.createScales();
        this.addGridlines();
        this.addAxes();
        this.addLines();
        this.addPoints();
        this.addRectangleForUptime();
        this.addRectangleForAnnotation();
        this.addEventPoints();
        this.addBoundaries();
        this.addZoom();
        this.addTooltip();
        this.addPreviewChart();
        this.addLegend();
        this.handleZoomButtons();
        this.handleChartServiceButtons();
        this.handleScaleButton();
        this.handleLoggersFilter();
    }
    static normalizeSelectorName(name) {
        const pattern = /[\u0300-\u036f]/g;
        const result = name.toLowerCase().replace(/\s/g, "")
            .normalize('NFD')
            .replace(pattern, '');
        const pattern2 = /[a-z|A-Z|0-9]/gi;
        const result2 = result.match(pattern2).join('');
        return result2;
    }
    get chartId() {
        return this._chartId;
    }
    get chartContainer() {
        return this._chartContainer;
    }
    get axisScales() {
        return this._axisScales;
    }
    getVisibleSources() {
        return [...new Set(this._dataManager.datasets.filter(x => x.isVisible == true).map(x => x.sourceId))];
    }
    addLegend() {
        if (this._chartConfiguration.hasLegend == false)
            return;
        const ctx = this;
        if (this._legendOrientation == LegendOrientation_1.LegendOrientation.ROW) {
            const legend = this._chartContainer.svgElement.append('g')
                .attr('class', 'legend')
                .attr('transform', 'translate(0,0)');
            const lg = legend.selectAll('g')
                .data(this._dataManager.datasets)
                .enter()
                .append('g')
                .style('cursor', 'pointer')
                .attr('transform', (d, i) => `translate(${i * 100},${ctx._settings.svgHeight + 15})`)
                .on('click', function (d) {
                ctx.onLegendClick(d);
            }).on('mouseover', function (d) {
                ctx.onLegendHover(d);
            }).on('mouseout', function (d) {
                ctx.onLegendHoverOut(d);
            });
            lg.append("svg")
                .attr('width', 13)
                .attr('height', 13)
                .attr('viewBox', '0 0 24 24')
                .attr('id', function (d) {
                return `svg-${Chart.normalizeSelectorName(d.name)}-${Chart.normalizeSelectorName(d.serviceName)}-${d.serviceId}`;
            })
                .insert("path")
                .style('fill', d => d.color)
                .attr("d", 'M0 0v24h24v-24h-24zm10.041 17l-4.5-4.319 1.395-1.435 3.08 2.937 7.021-7.183 1.422 1.409-8.418 8.591z')
                .attr('id', function (d) {
                return `checkbox-${Chart.normalizeSelectorName(d.name)}-${Chart.normalizeSelectorName(d.serviceName)}-${d.serviceId}`;
            });
            lg.append('text')
                .style('font-size', this._fontSize ? this._fontSize : '12px')
                .style('fill', 'black')
                .attr('x', 17.5)
                .attr('y', 10)
                .attr('id', function (d) {
                return `text-${Chart.normalizeSelectorName(d.name)}-${Chart.normalizeSelectorName(d.serviceName)}-${d.serviceId}`;
            })
                .text(function (d) {
                return `${d.name} ${d.serviceId == Services_1.Services.ANNOTATION || d.serviceId == Services_1.Services.UPTIME ?
                    "(" + d.serviceName + ")" :
                    "(" + d.serviceName + " " + ctx._servicesManager.findServiceById(d.serviceId).sign + ")"}`;
            });
            let offset = ctx._settings.margin.left;
            let heightOffset = 10;
            const maxWidth = ctx._fullWidth - ctx._settings.margin.left - ctx._settings.margin.right;
            lg.attr('transform', function () {
                if (offset + this.getBBox().width + 10 > maxWidth) {
                    offset = ctx._settings.margin.left;
                    heightOffset += 20;
                }
                const x = offset;
                offset += this.getBBox().width + 10;
                return `translate(${x},${ctx._settings.svgHeight + heightOffset})`;
            });
            let w = 0;
            try {
                w = parseInt(this._chartContainer.svgElement.style("width"));
            }
            catch (_a) {
                w = 0;
            }
            const h = +this._settings.svgHeight + heightOffset + 20;
            this._chartContainer.svgElement.attr("height", h);
            if (this._chartConfiguration.isInteractive == true)
                this._chartContainer.svgElement.attr("viewBox", "0 0 " + w + " " + h);
            legend.attr('transform', function () {
                return `translate(${(maxWidth - this.getBBox().width) / 2},${0})`;
            });
        }
        else {
            const COLUMN_LEGEND_X_POS = function (d, i, offset) { return ctx._settings.margin.left + offset; };
            const COLUMN_LEGEND_Y_POS = function (d, i, offset) { return (ctx._settings.svgHeight + i * 17) + offset; };
            const COLUMN_HALF_LEGEND_X_POS = function (d, i, offset) { return i % 2 > 0 ? (ctx._fullWidth / 2) + ctx._settings.margin.left + offset : ctx._settings.margin.left + offset; };
            const COLUMN_HALF_LEGEND_Y_POS = function (d, i, offset) { return (ctx._settings.svgHeight + ((i - (i % 2)) * 8)) + offset; };
            const COLUMN_ONE_QUARTER_LEGEND_X_POS = function (d, i, offset) {
                i = i + 1;
                if (i % 4 == 1)
                    return ctx._settings.margin.left + offset;
                else if (i % 4 == 2)
                    return (ctx._fullWidth * (1 / 4)) + ctx._settings.margin.left + offset;
                else if (i % 4 == 3)
                    return (ctx._fullWidth * (1 / 2)) + ctx._settings.margin.left + offset;
                else if (i % 4 == 0)
                    return (ctx._fullWidth * (3 / 4)) + ctx._settings.margin.left + offset;
            };
            const COLUMN_ONE_QUARTER_LEGEND_Y_POS = function (d, i, offset) { return (ctx._settings.svgHeight + ((i - (i % 4)) * 4)) + offset; };
            const COLUMN_ONE_THIRD_LEGEND_X_POS = function (d, i, offset) {
                i = i + 1;
                if (i % 3 == 1)
                    return ctx._settings.margin.left + offset;
                else if (i % 3 == 2)
                    return (ctx._fullWidth * (1 / 3)) + ctx._settings.margin.left + offset;
                else if (i % 3 == 0)
                    return (ctx._fullWidth * (2 / 3)) + ctx._settings.margin.left + offset;
            };
            const COLUMN_ONE_THIRD_LEGEND_Y_POS = function (d, i, offset) { return (ctx._settings.svgHeight + ((i - (i % 3)) * 5)) + offset; };
            this._chartContainer.svgElement.selectAll("legendDots")
                .data(this._dataManager.datasets)
                .enter()
                .append('svg')
                .style('fill', d => d.color)
                .style('cursor', 'pointer')
                .attr('x', function (d, i) {
                return ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN ? COLUMN_LEGEND_X_POS(d, i, 0) :
                    ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_HALF ? COLUMN_HALF_LEGEND_X_POS(d, i, 0) :
                        ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_ONE_THIRD ? COLUMN_ONE_THIRD_LEGEND_X_POS(d, i, 0) :
                            COLUMN_ONE_QUARTER_LEGEND_X_POS(d, i, 0);
            })
                .attr('y', function (d, i) {
                return ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN ? COLUMN_LEGEND_Y_POS(d, i, 5) :
                    ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_HALF ? COLUMN_HALF_LEGEND_Y_POS(d, i, 5) :
                        ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_ONE_THIRD ? COLUMN_ONE_THIRD_LEGEND_Y_POS(d, i, 5) :
                            COLUMN_ONE_QUARTER_LEGEND_Y_POS(d, i, 5);
            })
                .on('click', function (d) {
                ctx.onLegendClick(d);
            })
                .attr('width', 13)
                .attr('height', 13)
                .attr('viewBox', '0 0 24 24')
                .attr('id', function (d) {
                return `svg-${Chart.normalizeSelectorName(d.name)}-${Chart.normalizeSelectorName(d.serviceName)}-${d.serviceId}`;
            })
                .insert("path")
                .style('fill', d => d.color)
                .attr("d", 'M0 0v24h24v-24h-24zm10.041 17l-4.5-4.319 1.395-1.435 3.08 2.937 7.021-7.183 1.422 1.409-8.418 8.591z')
                .attr('id', function (d) {
                return `checkbox-${Chart.normalizeSelectorName(d.name)}-${Chart.normalizeSelectorName(d.serviceName)}-${d.serviceId}`;
            });
            this._chartContainer.svgElement.selectAll("legendLabels")
                .data(this._dataManager.datasets)
                .enter()
                .append("text")
                .style('font-size', this._fontSize ? this._fontSize : '12px')
                .style('cursor', 'pointer')
                .attr("x", function (d, i) {
                return ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN ? COLUMN_LEGEND_X_POS(d, i, 20) :
                    ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_HALF ? COLUMN_HALF_LEGEND_X_POS(d, i, 20) :
                        ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_ONE_THIRD ? COLUMN_ONE_THIRD_LEGEND_X_POS(d, i, 20) :
                            COLUMN_ONE_QUARTER_LEGEND_X_POS(d, i, 20);
            })
                .attr("y", function (d, i) {
                return ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN ? COLUMN_LEGEND_Y_POS(d, i, 15) :
                    ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_HALF ? COLUMN_HALF_LEGEND_Y_POS(d, i, 15) :
                        ctx._legendOrientation == LegendOrientation_1.LegendOrientation.COLUMN_ONE_THIRD ? COLUMN_ONE_THIRD_LEGEND_Y_POS(d, i, 15) :
                            COLUMN_ONE_QUARTER_LEGEND_Y_POS(d, i, 15);
            })
                .style("fill", "black")
                .attr('id', function (d) {
                return `text-${Chart.normalizeSelectorName(d.name)}-${Chart.normalizeSelectorName(d.serviceName)}-${d.serviceId}`;
            })
                .text(function (d) { return `${d.name} (${ctx._servicesManager.findServiceById(d.serviceId).sign})`; })
                .attr("text-anchor", "left")
                .on('click', function (d) {
                ctx.onLegendClick(d);
            });
            let heightOffset = null;
            switch (ctx._legendOrientation) {
                case LegendOrientation_1.LegendOrientation.COLUMN:
                    heightOffset = this._dataManager.datasets.length * 17;
                    break;
                case LegendOrientation_1.LegendOrientation.COLUMN_HALF:
                    heightOffset = (this._dataManager.datasets.length / 2) * 15;
                    break;
                case LegendOrientation_1.LegendOrientation.COLUMN_ONE_THIRD:
                    heightOffset = (this._dataManager.datasets.length * (1 / 3)) * 13;
                    break;
                case LegendOrientation_1.LegendOrientation.COLUMN_ONE_QUARTER:
                    heightOffset = (this._dataManager.datasets.length * (1 / 4)) * 15;
                    break;
            }
            let w = 0;
            try {
                w = parseInt(this._chartContainer.svgElement.style("width"));
            }
            catch (_b) {
                w = 0;
            }
            const h = +this._settings.svgHeight + heightOffset + 20;
            this._chartContainer.svgElement.attr("height", h);
            if (this._chartConfiguration.isInteractive == true)
                this._chartContainer.svgElement.attr("viewBox", "0 0 " + w + " " + h);
        }
    }
    makeResponsive() {
        const self = this;
        const chart = document.getElementById(self._chartId);
        if (chart) {
            self.destroyChart();
            self.createChart();
            self.loadSelectedService(self._initialService);
        }
    }
    handleZoomButtons() {
        if (this._chartConfiguration.hasZoomButtons == false)
            return;
        const self = this;
        (0, d3_1.selectAll)('.zoom_btn.' + self._chartId).on('click', zoomClick);
        function zoomClick() {
            if (this.id == "zoom_in")
                self.chartContainer.zoom.scaleBy(self.chartContainer.zoomableArea.transition().duration(250), 1.2);
            else if (this.id == "zoom_out")
                self.chartContainer.zoom.scaleBy(self.chartContainer.zoomableArea.transition().duration(250), 0.8);
        }
    }
    handleChartServiceButtons() {
        if (this._chartConfiguration.hasServiceButtons == false)
            return;
        const self = this;
        (0, d3_1.selectAll)('.chartServices.' + self._chartId).on('click', hideServices);
        function hideServices() {
            if (this.id == ChartServices_1.ChartServices.ALL + self._chartId) {
                self._initialService = ChartServices_1.ChartServices.ALL;
            }
            else if (this.id == ChartServices_1.ChartServices.TEMPERATURE + self._chartId) {
                self._initialService = ChartServices_1.ChartServices.TEMPERATURE;
            }
            else if (this.id == ChartServices_1.ChartServices.PRESSURE + self._chartId) {
                self._initialService = ChartServices_1.ChartServices.PRESSURE;
            }
            else if (this.id == ChartServices_1.ChartServices.HUMIDITY + self._chartId) {
                self._initialService = ChartServices_1.ChartServices.HUMIDITY;
            }
            else if (this.id == ChartServices_1.ChartServices.UPTIME + self._chartId) {
                self._initialService = ChartServices_1.ChartServices.UPTIME;
            }
            self.loadSelectedService(self._initialService);
            self.changeServiceParam(self._initialService);
        }
    }
    handleScaleButton() {
        if (this._chartConfiguration.hasScaleButton == false)
            return;
        const ctx = this;
        (0, d3_1.selectAll)('.chartScale.' + ctx._chartId).on('click', scaleByData);
        function scaleByData() {
            ctx.scaleByData();
        }
    }
    handleLoggersFilter() {
        if (this._chartConfiguration.hasLoggersFilter == false)
            return;
        const self = this;
        self._dataManager.datasets.forEach(dataset => {
            (0, d3_1.selectAll)('.checkbox-' + Chart.normalizeSelectorName(dataset.name) + '-' + self._chartId).on('click', changeCheckbox);
            function changeCheckbox() {
                if (this.checked) {
                    self._dataManager.datasets.forEach(x => {
                        if (x.name == dataset.name) {
                            x.isVisible = true;
                            x.isSelectedLoggerFilter = true;
                        }
                    });
                }
                else {
                    self._dataManager.datasets.forEach(x => {
                        if (x.name == dataset.name) {
                            x.isVisible = false;
                            x.isSelectedLoggerFilter = false;
                        }
                    });
                }
            }
        });
        (0, d3_1.selectAll)('.allCheckboxes-' + self._chartId).on('click', allCheckboxes);
        function allCheckboxes() {
            if (this.checked) {
                self._dataManager.datasets.forEach(dataset => {
                    dataset.isVisible = true;
                    dataset.isSelectedLoggerFilter = true;
                    const selectLoggerFilter = document.getElementById('checkbox-' + Chart.normalizeSelectorName(dataset.name) + '-' + self._chartId);
                    selectLoggerFilter.checked = true;
                });
            }
            else {
                self._dataManager.datasets.forEach(dataset => {
                    dataset.isVisible = false;
                    dataset.isSelectedLoggerFilter = false;
                    const selectLoggerFilter = document.getElementById('checkbox-' + Chart.normalizeSelectorName(dataset.name) + '-' + self._chartId);
                    selectLoggerFilter.checked = false;
                });
            }
        }
        (0, d3_1.selectAll)('.applyLoggerFilter-' + self._chartId).on('click', applyLoggerFilter);
        function applyLoggerFilter() {
            self.changeFiltersParam();
            self.scaleYaxis();
            self.rescaleElements();
            self.loadSelectedService(self._initialService);
        }
    }
    getCurrentURL() {
        return new URL(window.location.href);
    }
    getServiceParam(initService) {
        if (this._chartConfiguration.hasServiceButtons == false)
            return;
        let customInitService = ChartServices_1.ChartServices.TEMPERATURE;
        if (initService)
            customInitService = initService;
        const currentUrl = this.getCurrentURL();
        try {
            const givenTS = currentUrl.searchParams.get('service' + this._chartId);
            return Object.values(ChartServices_1.ChartServices).includes(givenTS) ? givenTS : customInitService;
        }
        catch (_a) {
            return customInitService;
        }
    }
    getLoggersFilterParam() {
        if (this._chartConfiguration.hasLoggersFilter == false && this._chartConfiguration.hasLegendsFilter == false)
            return;
        const self = this;
        const currentUrl = self.getCurrentURL();
        try {
            const loggerFilters = JSON.parse(currentUrl.searchParams.get('loggerFilters' + self._chartId));
            if (loggerFilters && loggerFilters.length > 0) {
                const filterdDatasets = [];
                loggerFilters.forEach(logger => {
                    const dataset = self._dataManager.datasets.find(dataset => Chart.normalizeSelectorName(dataset.name) == logger.name &&
                        dataset.serviceId == logger.serviceId &&
                        Chart.normalizeSelectorName(dataset.serviceName) == logger.serviceName);
                    filterdDatasets.push(dataset);
                });
                return filterdDatasets;
            }
            else {
                return [];
            }
        }
        catch (_a) {
            return [];
        }
    }
    scaleByData() {
        this._chartConfiguration.isScaleByData = !this._chartConfiguration.isScaleByData;
        this.scaleYaxis();
        this.rescaleElements();
        this.loadSelectedService(this._initialService);
    }
    loadSelectedloggers() {
        if (this._chartConfiguration.hasLegendsFilter == false)
            return;
        const self = this;
        const loggerFilters = self._loggerFilters.filter(item => item);
        if (loggerFilters.length > 0) {
            self._dataManager.datasets.filter(dataset => {
                let filteredDataset;
                loggerFilters.forEach(logger => {
                    if (Chart.normalizeSelectorName(dataset.name) == Chart.normalizeSelectorName(logger.name) &&
                        dataset.serviceId == logger.serviceId &&
                        Chart.normalizeSelectorName(dataset.serviceName) == Chart.normalizeSelectorName(logger.serviceName)) {
                        filteredDataset = logger;
                    }
                });
                if (filteredDataset) {
                    dataset.isVisible = true;
                    dataset.isSelectedLoggerFilter = true;
                }
                else {
                    dataset.isVisible = false;
                    dataset.isSelectedLoggerFilter = false;
                }
            });
            if (self._chartConfiguration.hasLoggersFilter) {
                self._dataManager.datasets.forEach(dataset => {
                    if (dataset.isVisible) {
                        const selectLoggerFilter = document.getElementById('checkbox-' + Chart.normalizeSelectorName(dataset.name) + '-' + this._chartId);
                        selectLoggerFilter.checked = true;
                    }
                });
            }
        }
    }
    changeSelectedService(service) {
        const self = this;
        self._dataManager.datasets.forEach(dataset => {
            if (dataset.serviceId == service && dataset.isSelectedLoggerFilter == true) {
                dataset.isVisible = true;
                self.changeChart(dataset);
            }
            else {
                dataset.isVisible = false;
                self.changeChart(dataset);
            }
        });
    }
    loadSelectedService(service) {
        if (this._chartConfiguration.hasServiceButtons == false)
            return;
        const self = this;
        switch (service) {
            case ChartServices_1.ChartServices.TEMPERATURE:
                self.changeSelectedService(Services_1.Services.TEMPERATURE);
                break;
            case ChartServices_1.ChartServices.HUMIDITY:
                self.changeSelectedService(Services_1.Services.HUMIDITY);
                break;
            case ChartServices_1.ChartServices.PRESSURE:
                self.changeSelectedService(Services_1.Services.PRESSURE);
                break;
            case ChartServices_1.ChartServices.UPTIME:
                self.changeSelectedService(Services_1.Services.UPTIME);
                break;
            default:
                self._dataManager.datasets.forEach(dataset => {
                    if (dataset.isSelectedLoggerFilter == true) {
                        dataset.isVisible = true;
                        self.changeChart(dataset);
                    }
                    else {
                        dataset.isVisible = false;
                        self.changeChart(dataset);
                    }
                });
                break;
        }
        self.selectServices(service);
    }
    destroyChart() {
        this._chartContainer.svgElement.remove();
        this.chartContainer.focusCircles = [];
        this._tooltipManager.removeTooltip();
        this._axisScales = [];
    }
    addPreviewChart() {
        const self = this;
        if (self._chartConfiguration.hasPreviewChart == false)
            return;
        const pressureServices = self._servicesManager.services.filter(service => service.id == Services_1.Services.PRESSURE);
        self._chartContainer.svgElement.append("defs").append("svg:clipPath")
            .attr("id", self.createWithChartSuffix("previewChart"))
            .append("svg:rect")
            .attr("width", self._dimensions.width)
            .attr("height", self._dimensions.heightPreview)
            .attr("x", 0)
            .attr("y", 0);
        self._chartContainer.previewChart = self._chartContainer.svgElement.append("g")
            .attr("class", self.createWithChartSuffix("context"))
            .attr("transform", "translate(" + self._settings.marginPreview.left + "," + self._settings.marginPreview.top + ")")
            .attr("clip-path", "url(#" + self.createWithChartSuffix("previewChart") + ")");
        self._servicesManager.services.forEach(service => {
            if (pressureServices.length > 1 && service.originalName == ServiceNames_1.ServiceNames.ATMOSPHERICPRESSURE) {
                return;
            }
            const lineDrawer = self._axesManager.createLineDrawerForService(service.id).linePreview;
            const datasets = self._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.LINE);
            datasets.forEach(dataset => {
                const filteredData = dataset.data.filter(lineDrawer.defined());
                self._chartContainer.previewChart.append("path")
                    .datum(filteredData)
                    .attr("class", self.createWithChartSuffix("line line-preview--" + service.name.split('').join('')))
                    .attr('id', `line-previewDashed-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("fill-opacity", 0)
                    .attr("stroke-width", "1.5px")
                    .attr("stroke", dataset.color)
                    .style("stroke-dasharray", "2,2")
                    .attr("d", lineDrawer);
                self._chartContainer.previewChart.append("path")
                    .datum(dataset.data)
                    .attr("class", self.createWithChartSuffix("line line-preview--" + service.name.split('').join('')))
                    .attr('id', `line-preview-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("fill-opacity", 0)
                    .attr("stroke-width", "1.5px")
                    .attr("stroke", dataset.color)
                    .attr("d", lineDrawer);
            });
            const AnnotationDatasets = self._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.ANNOTATIONRECTANGLE);
            AnnotationDatasets.forEach(dataset => {
                const localX = self._axesManager.x;
                self._chartContainer.previewChart
                    .selectAll("rect-annotation")
                    .data(dataset.data)
                    .enter()
                    .append("rect")
                    .attr("class", self.createWithChartSuffix("rect-annotation rect-preview-" + service.name.split(' ').join('')))
                    .attr('id', `rect-preview-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("width", function (d) {
                    return self.hasOnlyStartDate(d.time, d.toTime) ? 1 : Math.abs(localX(d.toTime) - localX(d.time));
                })
                    .attr("height", self._dimensions.height)
                    .attr("x", function (d) {
                    return localX(d.time);
                })
                    .attr("y", 0)
                    .style("opacity", 0.4)
                    .style("fill", dataset.color);
            });
            const rectDatasets = self._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.UPTIMERECTANGLE);
            rectDatasets.forEach(dataset => {
                const localX = self._axesManager.x;
                self._chartContainer.previewChart
                    .selectAll("rect")
                    .data(dataset.data)
                    .enter()
                    .append("rect")
                    .attr("class", self.createWithChartSuffix("rect rect-preview-" + service.name.split(' ').join('')))
                    .attr('id', `rect-preview-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("width", function (d) {
                    return Math.abs(localX(d.time + d.value) - localX(d.time));
                })
                    .attr("height", self._dimensions.height)
                    .attr("x", function (d) {
                    return localX(d.time);
                })
                    .attr("y", 0)
                    .style("opacity", 0.4)
                    .style("fill", dataset.color);
            });
            const pointDatasets = self._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.POINTS);
            pointDatasets.forEach((dataset) => {
                const localY = self._axesManager.findServiceAxisByService(service.id).yPreview;
                const localX = self._axesManager.x;
                self._chartContainer.previewChart
                    .selectAll("dot")
                    .data(dataset.data)
                    .enter()
                    .append("circle")
                    .attr("class", self.createWithChartSuffix("circle circle-preview-" + service.name.split(' ').join('')))
                    .attr('id', `circle-preview-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("r", 1)
                    .attr("cx", function (d) {
                    return localX(d.time);
                })
                    .attr("cy", function (d) {
                    return localY(d.value);
                })
                    .attr("stroke", "black")
                    .attr("stroke-width", "1px")
                    .style("fill", dataset.color);
            });
        });
        self._chartContainer.previewChart.append("g")
            .attr("class", "axis axis--x")
            .attr("color", "black")
            .attr("transform", "translate(0," + self._dimensions.heightPreview + ")")
            .call(self._axesManager.xAxisPreview);
        self._chartContainer.brush = (0, d3_1.brushX)()
            .extent([[0, 0], [self._dimensions.width, self._dimensions.heightPreview]])
            .on("brush end", self.brushed.bind(this));
        self._chartContainer.previewChart.append("g")
            .attr("class", "brush")
            .call(self._chartContainer.brush)
            .call(self._chartContainer.brush.move, self._axesManager.x.range());
    }
    addTooltip() {
        if (this._chartConfiguration.isInteractive == false)
            return;
        this._chartContainer.focusLine = this._chartContainer.chartBody
            .append("line")
            .attr("class", this.createWithChartSuffix("focusline"))
            .attr("style", "fill: none; stroke: grey; stroke-width: 3px; stroke-dasharray: 5, 5;")
            .attr("y1", 0)
            .attr("y2", this._dimensions.height)
            .style("display", "none");
        this._dataManager.datasets.forEach(dataset => {
            this._chartContainer.focusCircles.push(this._chartContainer.chartBody
                .append("circle")
                .attr("class", this.createWithChartSuffix("focusCircle"))
                .attr("fill", dataset.color)
                .attr("r", 4)
                .style("display", "none")
                .attr("pointer-events", "none"));
        });
        const tooltip = (0, d3_1.select)(this._chartContainer.svgElement.node().parentNode)
            .append("div")
            .attr("class", this.createWithChartSuffix("chart-tooltip"))
            .style("display", "none");
        this._tooltipManager = new TooltipManager_1.default(tooltip, this._dateFormatter, this._onlyTimeFormatter, this._onlyDateFormatter, this._fontSize);
    }
    addZoom() {
        if (this._chartConfiguration.isInteractive == false)
            return;
        const range = this._dataManager.getTimeRange(this._dateRange);
        const maxZoom = (range[1] - range[0]) / 3600000;
        this._chartContainer.zoom = (0, d3_1.zoom)()
            .scaleExtent([1, maxZoom])
            .translateExtent([[0, 0], [this._dimensions.width, this._dimensions.height]])
            .extent([[0, 0], [this._dimensions.width, this._dimensions.height]])
            .on("zoom", this.zoomed.bind(this));
        this.chartContainer.zoomableArea = this._chartContainer.svgElement.append("rect")
            .attr("class", "zoom")
            .attr("width", this._dimensions.width)
            .attr("height", this._dimensions.height)
            .attr("transform", "translate(" + this._settings.margin.left + "," + this._settings.margin.top + ")")
            .on('mousemove', this.drawTooltipElements.bind(this))
            .on('mouseout', this.removeTooltipElements.bind(this))
            .call(this._chartContainer.zoom);
    }
    addBoundaries() {
        if (this._chartConfiguration.hasBoundaries == false)
            return;
        if (this._boundaryManager.boundarySets.length < 1)
            throw "Can\'t draw empty boundaries";
        this._servicesManager.services.forEach(service => {
            const serviceAxis = this._axesManager.findServiceAxisByService(service.id);
            const boundarySet = this._boundaryManager.findBoundarySetByService(service.id);
            if (!boundarySet)
                return;
            const xPos = AxesManager_1.default.isAxisPulledRight(service.axisPosition) ? this._axesManager.getDomainX()[1] : this._axesManager.getDomainX()[0];
            this.addBoundaryLines(serviceAxis, boundarySet.values, null, service.name);
            this.addBoundaryDots(serviceAxis, this._axesManager.x, xPos, boundarySet.values, service.name);
            if (!this._servicesManager.isMultiservice())
                this.addBoundaryRect(serviceAxis, boundarySet);
        });
    }
    isCursorOut(positionTime, pointData, data, style) {
        const visibleTime = this._axesManager.x.domain()[1].getTime() - this._axesManager.x.domain()[0].getTime();
        const treshold = visibleTime * 0.01;
        if (style == DataVisualStyle_1.DataVisualStyle.POINTS) {
            if ((positionTime - treshold > pointData.time) || (pointData.time > positionTime))
                return true;
            else
                return false;
        }
        else if (style == DataVisualStyle_1.DataVisualStyle.LINE) {
            if ((positionTime < data[0].time) || (positionTime - treshold > data[data.length - 1].time))
                return true;
            else
                return false;
        }
        else if (style == DataVisualStyle_1.DataVisualStyle.UPTIMERECTANGLE) {
            if ((positionTime - treshold > pointData.time + pointData.value) || (pointData.time > positionTime))
                return true;
            else
                return false;
        }
        else if (style == DataVisualStyle_1.DataVisualStyle.ANNOTATIONRECTANGLE) {
            if ((positionTime - treshold > pointData.toTime) || (pointData.time > positionTime))
                return true;
            else
                return false;
        }
    }
    removeFocusCircle(circle, index) {
        this._tooltipManager.removeTooltipPoint(index);
        circle.style("display", 'none');
        return false;
    }
    removeTooltipElements() {
        if (this._chartConfiguration.isInteractive == false)
            return;
        this._chartContainer.focusLine.style("display", "none");
        this._chartContainer.chartBody.selectAll(this.createWithChartSuffix(".focusCircle")).style("display", "none");
        this._tooltipManager.hideTooltip();
    }
    resetEventPoints() {
        this._chartContainer.chartBody.selectAll(this.createWithChartSuffix(".eventPoint")).attr("r", 5);
        this._tooltipManager.removeEventPoint();
    }
    drawTooltipElements(arg, index, contextArray) {
        const linePosition = this._axesManager.x.invert((0, d3_1.mouse)(contextArray[index])[0]).getTime();
        this._chartContainer.focusLine.style("display", null).attr("transform", "translate(" + this._axesManager.x(linePosition) + "," + 0 + ")");
        this.resetEventPoints();
        const x0 = this._axesManager.x.invert((0, d3_1.mouse)(contextArray[index])[0]);
        this._dataManager.datasets.forEach((dataset, indexDateset) => {
            if (dataset.isVisible == false) {
                this._tooltipManager.removeTooltipPoint(indexDateset);
                return;
            }
            const i = bisectDate(dataset.data, x0, 1), d = dataset.data[i - 1];
            try {
                if (this.isCursorOut(x0.getTime(), d, dataset.data, dataset.style)) {
                    return this.removeFocusCircle(this._chartContainer.focusCircles[indexDateset], indexDateset);
                }
                if (d.value === null) {
                    return this.removeFocusCircle(this._chartContainer.focusCircles[indexDateset], indexDateset);
                }
                const weight = this._boundaryManager ? this._boundaryManager.getWeightForServiceValue(dataset.serviceId, d.value) : null;
                const yValue = this._axesManager.findServiceAxisByService(dataset.serviceId).y(d.value);
                this._chartContainer.focusCircles[indexDateset].attr("cx", this._axesManager.x(d.time)).attr("cy", yValue).attr("r", 6).attr("stroke", "black");
                this._chartContainer.focusCircles[indexDateset].style("display", null);
                const service = this._servicesManager.findServiceById(dataset.serviceId);
                let tooltipPoint = null;
                if (service.id == Services_1.Services.UPTIME) {
                    tooltipPoint = new TooltipPoint_1.TooltipPoint(indexDateset, "Offline (" + this.toHHMMSS(Math.floor((d.value / 1000))) + ")", d.time, null, weight, dataset, "", yValue);
                }
                else if (service.id == Services_1.Services.ANNOTATION) {
                    tooltipPoint = new TooltipPoint_1.TooltipPoint(indexDateset, d.value, d.time, d.toTime, weight, dataset, "", yValue);
                }
                else {
                    tooltipPoint = new TooltipPoint_1.TooltipPoint(indexDateset, d.value, d.time, null, weight, dataset, service.sign, yValue);
                }
                this._tooltipManager.addTooltipPoint(tooltipPoint);
            }
            catch (_a) {
                return this.removeFocusCircle(this._chartContainer.focusCircles[indexDateset], indexDateset);
            }
        });
        if (this._chartConfiguration.hasEventPoints == true)
            this._eventDatasetManager.eventDataset.forEach(eventsSet => {
                const i = bisectDate(eventsSet.data, x0, 1), d = eventsSet.data[i - 1];
                try {
                    if (this.isCursorOut(x0.getTime(), d, [], DataVisualStyle_1.DataVisualStyle.POINTS))
                        return this.resetEventPoints();
                    this._chartContainer.chartBody.select("#eventPoint" + d.time).attr("r", 10);
                    const yValue = this._axesManager.findServiceAxisByService(eventsSet.serviceId).y(d.value);
                    const eventPoint = new TooltipPoint_1.TooltipPoint(null, d.value, d.time, null, null, eventsSet, this._servicesManager.findServiceById(eventsSet.serviceId).sign, yValue);
                    this._tooltipManager.setEventPoint(eventPoint);
                }
                catch (_a) {
                    return this.resetEventPoints();
                }
            });
        const currentTooltipWidth = this._tooltipManager.getTooltipWidth();
        const windowWidth = window.innerWidth;
        this._tooltipWidth = currentTooltipWidth > 0 ? currentTooltipWidth : this._tooltipWidth;
        const currentOffset = windowWidth <= TABLET_BREAKPOINT ? TOOLTIP_DEFAULT_OFFSET_TABLET : TOOLTIP_DEFAULT_OFFSET_DESKTOP;
        const currentTooltipHeight = this._tooltipManager.getTooltipHeight();
        const toolLeftPos = (0, d3_1.mouse)(document.body)[0] >= this._tooltipWidth + currentOffset + TOOLTIP_X_PADDING
            ? (0, d3_1.mouse)(document.body)[0] - this._tooltipWidth - TOOLTIP_X_PADDING - currentOffset
            : windowWidth - (0, d3_1.mouse)(document.body)[0] >= this._tooltipWidth + currentOffset + TOOLTIP_X_PADDING ? (0, d3_1.mouse)(document.body)[0] + TOOLTIP_X_PADDING - currentOffset : 0;
        this._tooltipManager.showTooltip(toolLeftPos, (0, d3_1.mouse)(document.body)[1] - currentTooltipHeight - TOOLTIP_Y_PADDING);
    }
    zoomed() {
        if (d3_1.event.sourceEvent && d3_1.event.sourceEvent.type === "brush")
            return;
        const t = d3_1.event.transform;
        this._axesManager.x.domain(t.rescaleX(this._axesManager.xInitial).domain());
        this.removeTooltipElements();
        this.rescaleElements();
        this._chartContainer.chartArea.select(this.createWithChartSuffix(".axis--x")).call(this._axesManager.xAxis);
        this._chartContainer.chartArea.selectAll(this.createWithChartSuffix(".grid.grid--x.grid")).call(this._axesManager.make_x_gridlines()
            .tickSize(-this._dimensions.height).tickFormat(() => { return ""; }));
        if (this._chartContainer.previewChart !== null)
            this._chartContainer.previewChart.select(".brush").call(this._chartContainer.brush.move, this._axesManager.x.range().map(t.invertX, t));
    }
    brushed() {
        if (this._chartConfiguration.isInteractive == false || (d3_1.event.sourceEvent && d3_1.event.sourceEvent.type === "zoom"))
            return;
        const s = d3_1.event.selection || this._axesManager.xInitial.range();
        this._axesManager.x.domain(s.map(this._axesManager.xInitial.invert, this._axesManager.xInitial));
        this.removeTooltipElements();
        this.rescaleElements();
        this._chartContainer.chartArea.select(this.createWithChartSuffix(".axis--x")).call(this._axesManager.xAxis);
        this._chartContainer.chartArea.selectAll(this.createWithChartSuffix(".grid.grid--x.grid")).call(this._axesManager.make_x_gridlines()
            .tickSize(-this._dimensions.height).tickFormat(() => { return ""; }));
        this._chartContainer.svgElement.select(".zoom").call(this._chartContainer.zoom.transform, d3_1.zoomIdentity
            .scale(this._dimensions.width / (s[1] - s[0]))
            .translate(-s[0], 0));
    }
    rescaleElements() {
        const self = this;
        self._servicesManager.services.forEach(service => {
            const localX = self._axesManager.x;
            const localY = self._axesManager.findServiceAxisByService(service.id);
            if (service.id == Services_1.Services.UPTIME) {
                self._chartContainer.chartBody.selectAll(self.createWithChartSuffix(".rect-" + service.name.split(' ').join('')))
                    .attr("width", function (d) {
                    return Math.abs(localX(d.time + d.value) - localX(d.time));
                })
                    .attr("height", self._dimensions.height)
                    .attr("x", function (d) {
                    return localX(d.time);
                });
            }
            if (service.id == Services_1.Services.ANNOTATION) {
                self._chartContainer.chartBody.selectAll(self.createWithChartSuffix(".rect-" + service.name.split(' ').join('')))
                    .attr("width", function (d) {
                    return self.hasOnlyStartDate(d.time, d.toTime) ? 1 : Math.abs(localX(d.toTime) - localX(d.time));
                })
                    .attr("height", self._dimensions.height)
                    .attr("x", function (d) {
                    return localX(d.time);
                });
            }
            self._chartContainer.chartBody.selectAll(self.createWithChartSuffix(".line--" + service.name.split(' ').join('')))
                .attr("d", localY.line);
            self._chartContainer.chartBody.selectAll(self.createWithChartSuffix(".eventPoint"))
                .attr("cx", function (d) { return localX(d.time); })
                .attr("cy", function () { return localY.y(localY.y.domain()[0]); });
            self._chartContainer.chartBody
                .selectAll(self.createWithChartSuffix(".circle-" + service.name.split(' ').join('')))
                .attr("cx", function (d) {
                return localX(d.time);
            })
                .attr("cy", function (d) {
                return localY.y(d.value);
            });
        });
    }
    addBoundaryRect(service, boundarySet) {
        if (!boundarySet.weights)
            return;
        const boundaryPoints = boundarySet.values;
        const colors = this._colorGenerator.getBoundaryRectanglesColors(boundaryPoints.length);
        boundaryPoints.forEach((point, index) => {
            const start = service.y.domain()[0] > point ? service.y.domain()[0] : point;
            const endPoint = boundaryPoints.length > index + 1 ? boundaryPoints[index + 1] : service.y.domain()[1];
            const stop = service.y.domain()[1] < endPoint ? service.y.domain()[1] : endPoint;
            if (start > stop)
                return;
            this._chartContainer.chartBody.append("rect")
                .attr("class", this.createWithChartSuffix("boundaryRect"))
                .attr("width", this._dimensions.width)
                .attr("height", Math.abs(service.y(stop) - service.y(start)))
                .attr("x", 0)
                .attr("y", service.y(stop))
                .style("fill", colors[index])
                .style("opacity", 0.10);
        });
    }
    addBoundaryLines(service, boundaryPoints, colors, serviceName) {
        if (this._chartConfiguration.hasBoundaries == false)
            return;
        for (let j = 0; j < boundaryPoints.length; j++) {
            const line = this._chartContainer.chartBody.append("line")
                .attr("fill", "none")
                .attr("class", this.createWithChartSuffix("boundaryLine-" + serviceName.split(' ').join('') + "-" + j))
                .attr("stroke-width", "1px")
                .attr("stroke-dasharray", "5, 5")
                .attr("x1", 0)
                .attr("x2", this._dimensions.width)
                .attr("transform", "translate(" + 0 + "," + service.y(boundaryPoints[j]) + ")");
            if (colors != null) {
                line.attr("stroke", colors[j + 1]);
            }
            else {
                line.attr("stroke", "grey");
                line.attr("opacity", 0.4);
            }
        }
    }
    addBoundaryDots(service, x, xPos, boundaryPoints, serviceName) {
        if (this._chartConfiguration.hasBoundaries == false)
            return;
        this._chartContainer.chartBody.selectAll("dot")
            .data(boundaryPoints)
            .enter().append("circle")
            .attr("class", this.createWithChartSuffix("boundaryDot boundaryDot--" + serviceName.split(' ').join('')))
            .attr("fill", this._boundaryManager.pointColor)
            .attr("r", 5)
            .attr("cx", function () { return x(xPos); })
            .attr("cy", function (d) { return service.y(d); });
    }
    addEventPoints() {
        if (this._chartConfiguration.hasEventPoints == false)
            return;
        this._servicesManager.services.forEach(service => {
            const eventPointSets = this._eventDatasetManager.findEventPointSetByService(service.id);
            const localY = this._axesManager.findServiceAxisByService(service.id).y;
            const localX = this._axesManager.x;
            eventPointSets.forEach(events => {
                this._chartContainer.chartBody.selectAll("dot")
                    .data(events.data)
                    .enter().append("circle")
                    .attr("id", function (d) { return "eventPoint" + d.time; })
                    .attr("class", this.createWithChartSuffix("eventPoint"))
                    .attr("r", 5)
                    .attr("cx", function (d) { return localX(d.time); })
                    .attr("cy", function () { return localY(localY.domain()[0]); })
                    .attr("stroke", "black")
                    .attr("stroke-width", "1px")
                    .style("fill", events.color);
            });
        });
    }
    addPoints() {
        this._servicesManager.services.forEach((service) => {
            const datasets = this._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.POINTS);
            datasets.forEach((dataset) => {
                const localY = this._axesManager.findServiceAxisByService(service.id).y;
                const localX = this._axesManager.x;
                this._chartContainer.chartBody
                    .selectAll("dot")
                    .data(dataset.data)
                    .enter()
                    .append("circle")
                    .attr("class", this.createWithChartSuffix("circle circle-" + service.name.split(' ').join('')))
                    .attr('id', `circle-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("r", 1)
                    .attr("cx", function (d) {
                    return localX(d.time);
                })
                    .attr("cy", function (d) {
                    return localY(d.value);
                })
                    .attr("stroke", "black")
                    .attr("stroke-width", "1px")
                    .style("fill", dataset.color);
            });
        });
    }
    addLines() {
        const pressureServices = this._servicesManager.services.filter(service => service.id == Services_1.Services.PRESSURE);
        this._servicesManager.services.forEach(service => {
            if (pressureServices.length > 1 && service.originalName == ServiceNames_1.ServiceNames.ATMOSPHERICPRESSURE) {
                return;
            }
            const lineDrawer = this._axesManager.createLineDrawerForService(service.id).line;
            const datasets = this._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.LINE);
            datasets.forEach(dataset => {
                const filteredData = dataset.data.filter(lineDrawer.defined());
                this._chartContainer.chartBody.append("path")
                    .datum(filteredData)
                    .attr("class", this.createWithChartSuffix("line line--" + service.name.split(' ').join('')))
                    .attr('id', `lineDashed-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("fill-opacity", 0)
                    .attr("stroke-width", "1.5px")
                    .attr("stroke", dataset.color)
                    .style("stroke-dasharray", "2,2")
                    .attr("d", lineDrawer);
                this._chartContainer.chartBody.append("path")
                    .datum(dataset.data)
                    .attr("class", this.createWithChartSuffix("line line--" + service.name.split(' ').join('')))
                    .attr('id', `line-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("fill-opacity", 0)
                    .attr("stroke-width", "1.5px")
                    .attr("stroke", dataset.color)
                    .attr("d", lineDrawer);
            });
        });
    }
    addRectangleForUptime() {
        this._servicesManager.services.forEach(service => {
            const localX = this._axesManager.x;
            const datasets = this._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.UPTIMERECTANGLE);
            datasets.forEach(dataset => {
                this._chartContainer.chartBody
                    .selectAll("rect")
                    .data(dataset.data)
                    .enter()
                    .append("rect")
                    .attr("class", this.createWithChartSuffix("rect rect-" + service.name.split(' ').join('')))
                    .attr('id', `rect-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("width", function (d) {
                    return Math.abs(localX(d.time + d.value) - localX(d.time));
                })
                    .attr("height", this._dimensions.height)
                    .attr("x", function (d) {
                    return localX(d.time);
                })
                    .attr("y", 0)
                    .style("opacity", 0.4)
                    .style("fill", dataset.color);
            });
        });
    }
    addRectangleForAnnotation() {
        const self = this;
        self._servicesManager.services.forEach(service => {
            const localX = self._axesManager.x;
            const datasets = self._dataManager.findDatasetsByServiceIdAndStyle(service.id, DataVisualStyle_1.DataVisualStyle.ANNOTATIONRECTANGLE);
            datasets.forEach(dataset => {
                self._chartContainer.chartBody
                    .selectAll("rect-annotation")
                    .data(dataset.data)
                    .enter()
                    .append("rect")
                    .attr("class", self.createWithChartSuffix("rect-annotation rect-" + service.name.split(' ').join('')))
                    .attr('id', `rect-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`)
                    .attr("width", function (d) {
                    return self.hasOnlyStartDate(d.time, d.toTime) ? 1 : Math.abs(localX(d.toTime) - localX(d.time));
                })
                    .attr("height", self._dimensions.height)
                    .attr("x", function (d) {
                    return localX(d.time);
                })
                    .attr("y", 0)
                    .style("opacity", 0.4)
                    .style("fill", dataset.color);
            });
        });
    }
    hasOnlyStartDate(from, to) {
        from = from / 1000;
        to = to / 1000;
        return to - from < 60 ? true : false;
    }
    addGridlines() {
        if (this._chartConfiguration.hasXGrid == true) {
            this._chartContainer.chartArea.append("g")
                .attr("class", this.createWithChartSuffix("grid grid--x grid"))
                .attr("color", "#d3d3d3")
                .attr("transform", "translate(0," + this._dimensions.height + ")")
                .call(this._axesManager.make_x_gridlines()
                .tickSize(-this._dimensions.height)
                .tickFormat(() => { return ""; }));
        }
        if (this._chartConfiguration.hasYGrid == true) {
            this._chartContainer.chartArea.append("g")
                .attr("class", this.createWithChartSuffix("grid grid--y grid"))
                .attr("color", "#d3d3d3")
                .call(this._axesManager.make_y_gridlines(this._servicesManager.services[0].id)
                .tickSize(-this._dimensions.width)
                .tickFormat(() => { return ""; }));
        }
        this._chartContainer.chartArea.selectAll(".grid line")
            .attr("stroke", "#d3d3d3")
            .attr("stroke-opacity", 0.7);
        this._chartContainer.chartArea.selectAll(".grid path")
            .attr("stroke-width", 0);
    }
    addAxes() {
        const pressureServices = this._servicesManager.services.filter(service => service.id == Services_1.Services.PRESSURE);
        this._axesManager.createXAxis();
        this._chartContainer.chartArea.append("g")
            .attr("class", this.createWithChartSuffix("axis axis--x"))
            .attr("color", "black")
            .attr("transform", "translate(0," + this._dimensions.height + ")")
            .style('font-size', this._fontSize ? this._fontSize : '12px')
            .attr("stroke-width", this._axisThickness ? this._axisThickness : '1px')
            .call(this._axesManager.xAxis);
        this._servicesManager.services.forEach((service, serviceIndex) => {
            if (service.id == Services_1.Services.UPTIME || service.id == Services_1.Services.ANNOTATION) {
                return;
            }
            if (pressureServices.length > 1 && service.originalName == ServiceNames_1.ServiceNames.ATMOSPHERICPRESSURE) {
                return;
            }
            const isPulledRight = AxesManager_1.default.isAxisPulledRight(service.axisPosition);
            const label = this._chartContainer.chartArea
                .append("text")
                .style('font-size', this._fontSize ? this._fontSize : '12px')
                .attr("font-weight", "bold")
                .attr("color", "black")
                .attr("class", this.createWithChartSuffix(isPulledRight ?
                service.axisPosition == AxisPosition_1.AxisPosition.RIGHT_INSIDE ? 'y-axis-label-right-inside' : 'y-axis-label-right-outside'
                : service.axisPosition == AxisPosition_1.AxisPosition.LEFT_INSIDE ? 'y-axis-label-left-inside' : 'y-axis-label-left-outside'))
                .attr("x", 0 - this._dimensions.height / 2)
                .attr("dy", this._labelPosition == LabelPosition_1.LabelPosition.SIDE ? "0.9em" : "-0.7em")
                .style("text-anchor", "middle")
                .text(this._labelPosition == LabelPosition_1.LabelPosition.SIDE ? service.name + " (" + service.sign + ")" : service.sign);
            const yAxis = this._axesManager.createYAxis(service.id, service.axisPosition);
            this._chartContainer.chartArea.append("g")
                .attr("class", this.createWithChartSuffix(isPulledRight ?
                service.axisPosition == AxisPosition_1.AxisPosition.RIGHT_INSIDE ? 'axis y-axis-right-inside' : 'axis y-axis-right-outside'
                : service.axisPosition == AxisPosition_1.AxisPosition.LEFT_INSIDE ? 'axis y-axis-left-inside' : 'axis y-axis-left-outside'))
                .attr("color", "black")
                .style('font-size', this._fontSize ? this._fontSize : '12px')
                .attr("transform", isPulledRight ? "translate( " + this._dimensions.width + ", 0 )" : null)
                .attr("stroke-width", this._axisThickness ? this._axisThickness : '1px')
                .call(yAxis);
            if (this._labelPosition == LabelPosition_1.LabelPosition.UP) {
                const x_pos = serviceIndex >= 2 ? 15 : -10;
                label.attr("x", isPulledRight ?
                    service.axisPosition == AxisPosition_1.AxisPosition.RIGHT_INSIDE ? this._dimensions.width - 35 + this._settings.margin.left / 2 : this._dimensions.width - 10 + this._settings.margin.left / 2
                    : x_pos);
            }
            else {
                label.attr("transform", "rotate(-90)")
                    .attr("y", isPulledRight ? this._dimensions.width + this._settings.margin.left / 2 : 0 - this._settings.margin.left);
                if (serviceIndex >= 2)
                    label.attr("dy", "2.0em");
            }
        });
    }
    createScales() {
        this._axisScales = [];
        this._axesManager.createAndScaleX([0, this._dimensions.width], this._dataManager.getTimeRange(this._dateRange));
        this._servicesManager.services.forEach(service => {
            const boundarySet = this._boundaryManager ? this._boundaryManager.findBoundarySetByService(service.id) : null;
            const boundaryValues = boundarySet ? boundarySet.values : [];
            let datasetValueRange;
            if (this._axisScalesInput.length > 0) {
                const serviceAxisScale = this._axisScalesInput.find(x => x.serviceId == service.id);
                datasetValueRange = [serviceAxisScale.min, serviceAxisScale.max];
            }
            else {
                const valueRangeByUser = this._valueRangeArray.find(x => x.serviceId == service.id);
                if (valueRangeByUser) {
                    datasetValueRange = [valueRangeByUser.min, valueRangeByUser.max];
                }
                else {
                    const datasetsForService = this._dataManager.findDatasetsByServiceIdAndStyle(service.id);
                    const selecteddatasets = datasetsForService.filter(dataset => dataset.isVisible && dataset.isSelectedLoggerFilter);
                    const valueRange = DatasetEntityArrayManager_1.default.getValueRange(selecteddatasets.length > 0 ? selecteddatasets : datasetsForService);
                    if (this._chartConfiguration.hasBoundaries) {
                        if (this._chartConfiguration.isScaleByData) {
                            datasetValueRange = [valueRange[0], valueRange[1]];
                        }
                        else {
                            datasetValueRange = [Math.min(...boundaryValues, valueRange[0]), Math.max(...boundaryValues, valueRange[1])];
                        }
                    }
                    else {
                        datasetValueRange = [valueRange[0], valueRange[1]];
                    }
                }
            }
            if (datasetValueRange[0] == datasetValueRange[1]) {
                datasetValueRange[0] = datasetValueRange[0] - 1;
                datasetValueRange[1] = datasetValueRange[1] + 1;
            }
            const axisScale = {
                min: datasetValueRange[0],
                max: datasetValueRange[1],
                serviceId: service.id
            };
            this._axisScales.push(axisScale);
            this._axesManager.createAndScaleY(service.id, [this._dimensions.height, 0], datasetValueRange, [this._dimensions.heightPreview, 0]);
        });
    }
    createWithChartSuffix(name) {
        return name + this._chartId;
    }
    createChartContainer() {
        this._chartContainer.svgElement.append("defs").append("svg:clipPath")
            .attr("id", this.createWithChartSuffix("clip"))
            .append("svg:rect")
            .attr("width", this._dimensions.width)
            .attr("height", this._dimensions.height)
            .attr("x", 0)
            .attr("y", 0);
        this._chartContainer.chartArea = this._chartContainer.svgElement.append("g")
            .attr("class", this.createWithChartSuffix("focus"))
            .attr("transform", "translate(" + this._settings.margin.left + "," + this._settings.margin.top + ")");
        this._chartContainer.chartBody = this._chartContainer.svgElement.append("g")
            .attr("class", this.createWithChartSuffix("focus"))
            .attr("transform", "translate(" + this._settings.margin.left + "," + this._settings.margin.top + ")")
            .attr("clip-path", "url(#" + this.createWithChartSuffix("clip") + ")");
    }
    getElementWidth(element) {
        let width = +element.attr("width");
        if (isNaN(width))
            width = +element.node().getBoundingClientRect().width;
        return width;
    }
    createSvgElement(container, height, width) {
        let svg = null;
        if (container.node() && container.node().nodeName && container.node().nodeName.toLowerCase() == 'svg')
            svg = container.attr("width", width)
                .attr("height", height);
        else {
            svg = container.append("svg")
                .attr("width", width)
                .attr("height", height);
        }
        svg.attr("class", "loghub-chart");
        return svg;
    }
    countChartDimensions(element) {
        return {
            height: Math.abs(+element.attr("height") - this._settings.margin.top - this._settings.margin.bottom),
            width: Math.abs(this.getElementWidth(element) - this._settings.margin.left - this._settings.margin.right),
            heightPreview: this._settings.marginPreview != null ? +element.attr("height") - this._settings.marginPreview.top - this._settings.marginPreview.bottom : null
        };
    }
    onLegendClick(dataset) {
        if (this._chartConfiguration.hasLegendsFilter == false)
            return;
        const self = this;
        dataset.isVisible = !dataset.isVisible;
        if (dataset.isVisible) {
            dataset.isSelectedLoggerFilter = true;
            if (self._chartConfiguration.hasLoggersFilter == true) {
                const selectLoggerFilter = document.getElementById('checkbox-' + Chart.normalizeSelectorName(dataset.name) + '-' + this._chartId);
                selectLoggerFilter.checked = true;
            }
        }
        else {
            dataset.isSelectedLoggerFilter = false;
            const pendingSelection = self._dataManager.datasets.filter(x => x.name == dataset.name &&
                ((x.isVisible && x.isSelectedLoggerFilter) ||
                    (!x.isVisible && x.isSelectedLoggerFilter)));
            if (pendingSelection.length === 0) {
                if (self._chartConfiguration.hasLoggersFilter == true) {
                    const selectLoggerFilter = document.getElementById('checkbox-' + Chart.normalizeSelectorName(dataset.name) + '-' + this._chartId);
                    selectLoggerFilter.checked = false;
                }
            }
        }
        const service = self._initialService;
        if (service !== ChartServices_1.ChartServices.ALL) {
            const services = self._dataManager.datasets.filter(dataset => dataset.isSelectedLoggerFilter && dataset.isVisible).map(dataset => dataset.serviceId);
            if (services.length > 1) {
                const allSame = services.every((val) => val === services[0]);
                if (!allSame) {
                    self.selectServices(ChartServices_1.ChartServices.ALL);
                }
            }
            else if (services.length == 1) {
                const serviceById = self.getServiceNameById(services[0]);
                if (serviceById != service) {
                    self.selectServices(serviceById);
                }
            }
        }
        self.changeFiltersParam();
        self.scaleYaxis();
        self.rescaleElements();
        self.changeChart(dataset);
    }
    getServiceNameById(number) {
        switch (number) {
            case Services_1.Services.TEMPERATURE:
                return ChartServices_1.ChartServices.TEMPERATURE;
                break;
            case Services_1.Services.HUMIDITY:
                return ChartServices_1.ChartServices.HUMIDITY;
                break;
            case Services_1.Services.PRESSURE:
                return ChartServices_1.ChartServices.PRESSURE;
                break;
            case Services_1.Services.UPTIME:
                return ChartServices_1.ChartServices.UPTIME;
                break;
        }
    }
    scaleYaxis() {
        const pressureServices = this._servicesManager.services.filter(service => service.id == Services_1.Services.PRESSURE);
        this._servicesManager.services.forEach(service => {
            if (service.id == Services_1.Services.UPTIME || service.id == Services_1.Services.ANNOTATION) {
                return;
            }
            if (pressureServices.length > 1 && service.originalName == ServiceNames_1.ServiceNames.ATMOSPHERICPRESSURE) {
                return;
            }
            const boundarySet = this._boundaryManager ? this._boundaryManager.findBoundarySetByService(service.id) : null;
            const boundaryValues = boundarySet ? boundarySet.values : [];
            let datasetValueRange;
            if (this._axisScalesInput.length > 0) {
                const serviceAxisScale = this._axisScalesInput.find(x => x.serviceId == service.id);
                datasetValueRange = [serviceAxisScale.min, serviceAxisScale.max];
            }
            else {
                const valueRangeByUser = this._valueRangeArray.find(x => x.serviceId == service.id);
                if (valueRangeByUser) {
                    datasetValueRange = [valueRangeByUser.min, valueRangeByUser.max];
                }
                else {
                    const datasetsForService = this._dataManager.findDatasetsByServiceIdAndStyle(service.id);
                    const selecteddatasets = datasetsForService.filter(dataset => dataset.isVisible && dataset.isSelectedLoggerFilter);
                    const valueRange = DatasetEntityArrayManager_1.default.getValueRange(selecteddatasets.length > 0 ? selecteddatasets : datasetsForService);
                    if (this._chartConfiguration.hasBoundaries) {
                        if (this._chartConfiguration.isScaleByData) {
                            datasetValueRange = [valueRange[0], valueRange[1]];
                        }
                        else {
                            datasetValueRange = [Math.min(...boundaryValues, valueRange[0]), Math.max(...boundaryValues, valueRange[1])];
                        }
                    }
                    else {
                        datasetValueRange = [valueRange[0], valueRange[1]];
                    }
                }
            }
            if (datasetValueRange[0] == datasetValueRange[1]) {
                datasetValueRange[0] = datasetValueRange[0] - 1;
                datasetValueRange[1] = datasetValueRange[1] + 1;
            }
            const isPulledRight = AxesManager_1.default.isAxisPulledRight(service.axisPosition);
            this._axesManager.rescaleY(service.id, [this._dimensions.height, 0], datasetValueRange);
            const yAxis = this._axesManager.createYAxis(service.id, service.axisPosition);
            this._chartContainer.chartArea.select(this.createWithChartSuffix(isPulledRight ?
                service.axisPosition == AxisPosition_1.AxisPosition.RIGHT_INSIDE ? '.y-axis-right-inside' : '.y-axis-right-outside'
                : service.axisPosition == AxisPosition_1.AxisPosition.LEFT_INSIDE ? '.y-axis-left-inside' : '.y-axis-left-outside')).call(yAxis);
            this._chartContainer.chartArea.selectAll(this.createWithChartSuffix(".grid.grid--y.grid")).call(this._axesManager.make_y_gridlines(service.id)
                .tickSize(-this._dimensions.width).tickFormat(() => { return ""; }));
        });
        this.changeBoundaries();
    }
    changeBoundaries() {
        if (this._chartConfiguration.hasBoundaries == false)
            return;
        if (this._boundaryManager.boundarySets.length < 1)
            throw "Can\'t draw empty boundaries";
        this._servicesManager.services.forEach(service => {
            const serviceAxis = this._axesManager.findServiceAxisByService(service.id);
            const boundarySet = this._boundaryManager.findBoundarySetByService(service.id);
            if (!boundarySet)
                return;
            const xPos = AxesManager_1.default.isAxisPulledRight(service.axisPosition) ? this._axesManager.getDomainX()[1] : this._axesManager.getDomainX()[0];
            this.changeBoundaryLines(serviceAxis, boundarySet.values, null, service.name);
            this.changeBoundaryDots(serviceAxis, this._axesManager.x, xPos, boundarySet.values, service.name);
            if (!this._servicesManager.isMultiservice())
                this.changeBoundaryRect(serviceAxis, boundarySet);
        });
    }
    changeBoundaryRect(service, boundarySet) {
        if (!boundarySet.weights)
            return;
        const boundaryPoints = boundarySet.values;
        const colors = this._colorGenerator.getBoundaryRectanglesColors(boundaryPoints.length);
        boundaryPoints.forEach((point, index) => {
            const start = service.y.domain()[0] > point ? service.y.domain()[0] : point;
            const endPoint = boundaryPoints.length > index + 1 ? boundaryPoints[index + 1] : service.y.domain()[1];
            const stop = service.y.domain()[1] < endPoint ? service.y.domain()[1] : endPoint;
            if (start > stop)
                return;
            this._chartContainer.chartBody.selectAll(this.createWithChartSuffix(".boundaryRect"))
                .attr("width", this._dimensions.width)
                .attr("height", Math.abs(service.y(stop) - service.y(start)))
                .attr("x", 0)
                .attr("y", service.y(stop))
                .style("fill", colors[index])
                .style("opacity", 0.10);
        });
    }
    changeBoundaryDots(service, x, xPos, boundaryPoints, serviceName) {
        if (this._chartConfiguration.hasBoundaries == false)
            return;
        this._chartContainer.chartBody.selectAll(this.createWithChartSuffix(".boundaryDot.boundaryDot--" + serviceName.split(' ').join('')))
            .data(boundaryPoints)
            .attr("fill", this._boundaryManager.pointColor)
            .attr("r", 5)
            .attr("cx", function () { return x(xPos); })
            .attr("cy", function (d) { return service.y(d); });
    }
    changeBoundaryLines(service, boundaryPoints, colors, serviceName) {
        if (this._chartConfiguration.hasBoundaries == false)
            return;
        for (let j = 0; j < boundaryPoints.length; j++) {
            const line = this._chartContainer.chartBody.selectAll(this.createWithChartSuffix(".boundaryLine-" + serviceName.split(' ').join('') + "-" + j))
                .attr("x1", 0)
                .attr("x2", this._dimensions.width)
                .attr("transform", "translate(" + 0 + "," + service.y(boundaryPoints[j]) + ")");
            if (colors != null) {
                line.attr("stroke", colors[j + 1]);
            }
            else {
                line.attr("opacity", 0.4);
            }
        }
    }
    changeFiltersParam() {
        const self = this;
        let loggerFilters = [];
        self._dataManager.datasets.forEach(dataset => {
            if (dataset.isSelectedLoggerFilter) {
                loggerFilters.push({
                    name: Chart.normalizeSelectorName(dataset.name),
                    serviceId: dataset.serviceId,
                    serviceName: Chart.normalizeSelectorName(dataset.serviceName)
                });
            }
        });
        loggerFilters = loggerFilters.filter((item, index) => loggerFilters.indexOf(item) === index);
        const currentUrl = self.getCurrentURL();
        currentUrl.searchParams.set("loggerFilters" + self._chartId, JSON.stringify(loggerFilters));
        history.pushState({}, "", currentUrl);
    }
    changeServiceParam(service) {
        const currentUrl = this.getCurrentURL();
        currentUrl.searchParams.set("service" + this._chartId, service);
        history.pushState({}, "", currentUrl);
    }
    selectServices(service) {
        const tempServices = document.getElementById(ChartServices_1.ChartServices.TEMPERATURE + this._chartId);
        const humidityServices = document.getElementById(ChartServices_1.ChartServices.HUMIDITY + this._chartId);
        const pressureServices = document.getElementById(ChartServices_1.ChartServices.PRESSURE + this._chartId);
        const uptimeServices = document.getElementById(ChartServices_1.ChartServices.UPTIME + this._chartId);
        const allServices = document.getElementById(ChartServices_1.ChartServices.ALL + this._chartId);
        if (tempServices)
            tempServices.classList.remove('is-active');
        if (humidityServices)
            humidityServices.classList.remove('is-active');
        if (pressureServices)
            pressureServices.classList.remove('is-active');
        if (uptimeServices)
            uptimeServices.classList.remove('is-active');
        if (allServices)
            allServices.classList.remove('is-active');
        switch (service) {
            case ChartServices_1.ChartServices.TEMPERATURE:
                if (tempServices)
                    tempServices.classList.add('is-active');
                break;
            case ChartServices_1.ChartServices.HUMIDITY:
                if (humidityServices)
                    humidityServices.classList.add('is-active');
                break;
            case ChartServices_1.ChartServices.PRESSURE:
                if (pressureServices)
                    pressureServices.classList.add('is-active');
                break;
            case ChartServices_1.ChartServices.UPTIME:
                if (uptimeServices)
                    uptimeServices.classList.add('is-active');
                break;
            case ChartServices_1.ChartServices.ALL:
                if (allServices)
                    allServices.classList.add('is-active');
                break;
        }
        this._initialService = service;
        this.changeServiceParam(service);
    }
    changeChart(dataset) {
        const checkboxLegend = this._chartContainer.svgElement.select(`#checkbox-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const textLegend = this._chartContainer.svgElement.select(`#text-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const line = this._chartContainer.svgElement.select(`#line-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const lineDashed = this._chartContainer.svgElement.select(`#lineDashed-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const linePreview = this._chartContainer.svgElement.select(`#line-preview-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const linePreviewDashed = this._chartContainer.svgElement.select(`#line-previewDashed-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const points = this._chartContainer.svgElement.selectAll(`#circle-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const pointsPreview = this._chartContainer.previewChart.selectAll(`#circle-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const rect = this._chartContainer.svgElement.selectAll(`#rect-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        const rectPreview = this._chartContainer.previewChart.selectAll(`#rect-preview-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
        checkboxLegend.attr('d', dataset.isVisible == true ? 'M0 0v24h24v-24h-24zm10.041 17l-4.5-4.319 1.395-1.435 3.08 2.937 7.021-7.183 1.422 1.409-8.418 8.591z' : 'M0 0v24h24v-24h-24zm16.597 17.954l-4.591-4.55-4.555 4.596-1.405-1.405 4.547-4.592-4.593-4.552 1.405-1.405 4.588 4.543 4.545-4.589 1.416 1.403-4.546 4.587 4.592 4.548-1.403 1.416z');
        checkboxLegend.style('opacity', dataset.isVisible == true ? '1' : '.15');
        textLegend.style('fill', dataset.isVisible == true ? 'black' : '#979797');
        line.style('visibility', dataset.isVisible == true ? 'visible' : 'hidden');
        lineDashed.style('visibility', dataset.isVisible == true ? 'visible' : 'hidden');
        linePreview.style('visibility', dataset.isVisible == true ? 'visible' : 'hidden');
        linePreviewDashed.style('visibility', dataset.isVisible == true ? 'visible' : 'hidden');
        points.style("visibility", dataset.isVisible == true ? "visible" : "hidden");
        pointsPreview.style("visibility", dataset.isVisible == true ? "visible" : "hidden");
        rect.style("visibility", dataset.isVisible == true ? "visible" : "hidden");
        rectPreview.style("visibility", dataset.isVisible == true ? "visible" : "hidden");
    }
    onLegendHover(dataset) {
        if (dataset.serviceId == 5 || dataset.serviceId == 101) {
            const rect = this._chartContainer.svgElement.selectAll(`#rect-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
            rect.style("opacity", 1);
        }
        else {
            const line = this._chartContainer.svgElement.select(`#line-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
            line.attr("stroke-width", "3.5px");
        }
    }
    onLegendHoverOut(dataset) {
        if (dataset.serviceId == 5 || dataset.serviceId == 101) {
            const rect = this._chartContainer.svgElement.selectAll(`#rect-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
            rect.style("opacity", 0.4);
        }
        else {
            const line = this._chartContainer.svgElement.select(`#line-${Chart.normalizeSelectorName(dataset.name)}-${Chart.normalizeSelectorName(dataset.serviceName)}-${dataset.serviceId}`);
            line.attr("stroke-width", "1.5px");
        }
    }
    toHHMMSS(secs) {
        let hours = Math.floor(secs / 3600);
        let minutes = Math.floor((secs - (hours * 3600)) / 60);
        let seconds = secs - (hours * 3600) - (minutes * 60);
        if (hours < 10) {
            hours = "0" + hours;
        }
        if (minutes < 10) {
            minutes = "0" + minutes;
        }
        if (seconds < 10) {
            seconds = "0" + seconds;
        }
        return hours + ':' + minutes + ':' + seconds;
    }
}
exports.Chart = Chart;
